import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { cn } from "../../../common/u1/cn";
import FinancialCharts from "../../../common/FinancialCharts";
import { useGetCompanyFinanceChartDataMutation } from "../../../../store/Company/companyApiSlice";
import { currencyDetails } from "../../../../utils/currency";
import { StatusGlyph, ChartIcon } from "../../../../assets/ui/icons";
import { ProfileField } from "./ProfilePrimitives";
import { financialAdmin } from "./businessDetailsFields";

/* Tab 03 — Financials. The charts are the EXISTING component, rendered as it stands: common/FinancialCharts already fetches the finance series and draws all... */

/* Transcribed from FinancialCharts' axis label: the stored currency is a NAME
   ("UAE dirham") and the strip shows its code. */
export const currencyLabel = (currency) => {
  if (!currency) return "";
  const match = currencyDetails.find((c) => c?.name === currency);
  return match ? `${match.cc} (${match.name})` : String(currency);
};

export const FinancialsStrip = ({ finance, company }) => {
  const admin = financialAdmin(company);
  const currency = currencyLabel(finance?.currency);
  const units = finance?.units;
  const verified = Boolean(finance?.is_currency_verified);

  /* Every value is checked, not just the first: a strip of four labels with
     nothing under them reads as data that failed to load. Trimmed, because
     some cleared fields come back as " ". */
  const has = (v) => typeof v === "string" ? v.trim() !== "" : Boolean(v);
  if (![currency, units, admin?.lastAudited, admin?.auditFirm].some(has)) return null;

  return (
    <div
      className={cn(
        "flex flex-wrap items-start gap-x-8 gap-y-4 border-b border-[var(--line)] pb-5"
      )}
    >
      <ProfileField label="Currency">{currency}</ProfileField>
      <ProfileField label="Units">{units}</ProfileField>
      <ProfileField label="Last Audited">{admin?.lastAudited}</ProfileField>
      <ProfileField label="Audit Firm">{admin?.auditFirm}</ProfileField>

      {/* Only claimed when the flag says so — it is an assurance about the
          figures, not decoration. */}
      {verified && (
        <span
          className={cn(
            "mt-4 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1",
            "bg-[var(--success-soft)] text-[12px] font-semibold text-[var(--success)] font-sans"
          )}
        >
          <StatusGlyph kind="verified" size={13} />
          Currency verified
        </span>
      )}
    </div>
  );
};

export const FinancialsTab = ({ company, companyId }) => {
  const { token } = useSelector((state) => state.auth);
  const [getFinance] = useGetCompanyFinanceChartDataMutation();
  const [finance, setFinance] = useState(undefined);

  /* FinancialCharts calls `setShowUploadInput` unconditionally, so it is a required prop in practice — omitting it throws "setShowUploadInput is not a function"... */
  const [noCharts, setNoCharts] = useState(false);

  /* Read for the strip only. FinancialCharts fetches the same endpoint for itself — it takes a `resData` prop but only honours it in edit mode, and bending that... */
  useEffect(() => {
    const id = companyId;
    if (!token || !String(id || "").startsWith("TRX")) return undefined;

    let cancelled = false;
    getFinance({ token, company_id: id, location: "company-profile" })
      .unwrap()
      .then((res) => {
        if (!cancelled) setFinance(res);
      })
      .catch(() => {
        /* The strip is a caption on the charts below. If it cannot be read the
           charts still render and say their own piece, so there is nothing to
           tell the viewer — the strip simply does not draw. */
      });

    return () => {
      cancelled = true;
    };
  }, [companyId, token, getFinance]);

  return (
    <section className="rounded-[14px] border border-[var(--line)] bg-white p-5 sm:p-6">
      {/* The strip captions the charts. With no charts there is nothing to
          caption, so it goes rather than standing over an empty box. */}
      {!noCharts && <FinancialsStrip finance={finance} company={company} />}

      {/* The existing charts. Its own wrapper is a white card with its own padding, so the negative margin pulls it flush inside this one instead of nesting two... */}
      <div className={cn("-mx-4 -mb-3 mt-1 sm:-mx-5", noCharts && "hidden")}>
        <FinancialCharts companyId={companyId} setShowUploadInput={setNoCharts} />
      </div>

      {noCharts && <NoFinancials />}
    </section>
  );
};

/* Nothing published. A profile viewer cannot upload anything, so this says what
   is true and offers nothing — the live screen's upload prompt belongs to the
   owner's edit flow. */
export const NoFinancials = () => (
  <div className="py-10 text-center">
    <span
      aria-hidden="true"
      className={cn(
        "mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full",
        "bg-[var(--slate-2)] text-[var(--muted)]"
      )}
    >
      <ChartIcon size={26} />
    </span>
    <p className="m-0 text-[15px] font-bold text-[var(--ink)] font-sans">
      No financials published yet
    </p>
    <p className="mx-auto mt-1.5 mb-0 max-w-[340px] text-[13.5px] leading-[1.5] text-[var(--muted)] font-sans">
      This business has not published audited figures. Charts appear here once it
      does.
    </p>
  </div>
);

export default FinancialsTab;
